import React, { useState, useEffect } from "react";
import { SEARCH } from "../../core/theme/searchTheme";
import { buildScanRequest } from "../../core/data/scanPlan";
import useSearchData, { useNiches, usePostcodeDistricts } from "./useSearchData";
import { useAccount } from "../../core/hooks/useAccount";
import { maxPostcodesFor } from "../../core/data/account";
import useScanPlan from "./useScanPlan";
import KeywordsSection from "./components/KeywordsSection";
import NicheSection from "./components/NicheSection";
import LocationsSection from "./components/LocationsSection";
import BusinessSection from "./components/BusinessSection";
import SamplesSection from "./components/SamplesSection";
import ScanSummary from "./components/ScanSummary";
import SearchMap from "./components/SearchMap";
import SearchTabs from "./components/SearchTabs";
import CsvUpload from "./components/CsvUpload";
import useCsvUpload from "./useCsvUpload";

// Sample times in the summary are worked out from now, refreshed this often.
const CLOCK_REFRESH_MS = 30 * 1000;

// The search page: setup column on the left, map on the right.
// Fits the window exactly; only the setup column scrolls, if it must.
// Its values live in useScanForm (passed in as "form").
export default function SearchScreen({ form, userId, onStart, onOpenScans }) {
  const [now, setNow] = useState(() => new Date());
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  // The niche comes from the keywords.
  const niches = useNiches();
  const districts = usePostcodeDistricts();
  const account = useAccount();
  const plan = useScanPlan(form, niches, now, districts, maxPostcodesFor(account));
  const data = useSearchData({
    userId,
    nicheId: plan.niche ? plan.niche.id : null,
    standardForms: plan.standardForms,
  });

  const upload = useCsvUpload({ districts, niches, maxPostcodes: maxPostcodesFor(account) });

  const run = async () => {
    setRunning(true);
    await onStart(buildScanRequest({
      name: plan.name,
      keywords: plan.tidied,
      postcodes: plan.postcodes,
      niche: plan.runNiche,
      subGroup: plan.subGroup,
      suggestionId: plan.suggestion ? plan.suggestion.id : null,
      business: form.business,
      samples: form.samples,
      spacing: form.spacing,
    }));
    setRunning(false);
  };

  if (form.mode === "csv") return <CsvUpload form={form} upload={upload} onOpenScans={onOpenScans} />;

  return (
    <div className={SEARCH.page}>
      <div className={SEARCH.setupColumn}>
        <div className={SEARCH.setupScroll}>
          <SearchTabs mode={form.mode} setMode={form.setMode} />
          <KeywordsSection form={form} plan={plan} history={data.history} />
          <NicheSection form={form} plan={plan} niches={niches} nicheJobs={data.nicheJobs} saveSuggestion={data.saveSuggestion} />
          <BusinessSection form={form} />
          <LocationsSection form={form} plan={plan} />
          <SamplesSection form={form} />
        </div>
        <ScanSummary form={form} plan={plan} now={now} running={running} onRun={run} />
      </div>
      <SearchMap postcodes={plan.postcodes} centres={plan.centres} radius={form.radius} business={form.business} />
    </div>
  );
}
